import { containsHtmlBlockTags } from "@signalist/shared-types";

const HTML_ESCAPES: Record<string, string> = {
	"&": "&amp;",
	"<": "&lt;",
	">": "&gt;",
	'"': "&quot;",
	"'": "&#39;",
};

const escapeHtml = (text: string): string =>
	text.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char);

/**
 * Prepares a stored conversation body for rendering as HTML in the chat log.
 *
 * A body is one of two things, and the difference is invisible at the call site:
 *
 * - **HTML** — every `mail_sent` body (the API assembles it), and almost every
 *   `mail_received` one. Returned untouched: its newlines are the formatting
 *   whitespace of a document and must keep collapsing.
 * - **Plain text** — LinkedIn messages both sent and received (Unipile speaks
 *   plain text, and `convertHtmlToLinkedInText` emits it), invite notes, and any
 *   not-yet-sent draft typed into the composer's `<textarea>`. Its newlines are
 *   the author's own line breaks, so they become `<br>`; without this the body
 *   renders as one continuous line (PREC-3956).
 *
 * Plain text is HTML-escaped on the way, because the result is assigned to
 * `innerHTML`: a stranger's LinkedIn reply containing `<` would otherwise be
 * parsed as markup rather than shown as the character they typed.
 *
 * The HTML-vs-plain-text test itself is `containsHtmlBlockTags` from
 * `@signalist/shared-types` — the same one the API uses when sending, so the
 * two sides cannot drift.
 */
export function messageBodyToHtml(body: string | null | undefined): string {
	if (!body) {
		return "";
	}

	if (containsHtmlBlockTags(body)) {
		return body;
	}

	return escapeHtml(body.replace(/\r\n?/g, "\n")).replace(/\n/g, "<br>");
}
